import React, { useState, useEffect, useRef } from 'react';
import { Tab } from '@headlessui/react';
import InstancesList from '../components/haproxy/InstancesList';
import BackendsList from '../components/haproxy/BackendsList';
import DomainMappingsList from '../components/haproxy/DomainMappingsList';
import InstanceForm from '../components/haproxy/InstanceForm';
import DomainMappingForm from '../components/haproxy/DomainMappingForm';
import DeploymentModal from '../components/haproxy/DeploymentModal';
import DeploymentHistoryModal from '../components/haproxy/DeploymentHistoryModal';
import TemplatesList from '../components/haproxy/TemplatesList';
import api from '../services/api';
import { HAProxyInstance, HAProxyBackend, HAProxyServer, DomainMapping, HAProxyTemplate } from '../types';
import sseService from '../services/sseService';
import { ClockIcon, CloudArrowUpIcon, ServerIcon, ComputerDesktopIcon, GlobeAltIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

const API_BASE_URL =
  import.meta.env.PROD
    ? '/api'
    : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api');

const tabLabels = [
  { label: 'Instances', icon: ServerIcon },
  { label: 'Backends', icon: ComputerDesktopIcon },
  { label: 'Domain Mappings', icon: GlobeAltIcon },
  { label: 'Templates', icon: DocumentTextIcon },
];

const HAProxyPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState({
    instances: true,
    backends: true,
    domainMappings: true,
    templates: true
  });
  const [error, setError] = useState<string | null>(null);
  
  // State
  const [instances, setInstances] = useState<HAProxyInstance[]>([]);
  const [backends, setBackends] = useState<HAProxyBackend[]>([]);
  const [domainMappings, setDomainMappings] = useState<DomainMapping[]>([]);
  const [templates, setTemplates] = useState<HAProxyTemplate[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingInstance, setEditingInstance] = useState<HAProxyInstance | null>(null);
  const [isDomainMappingFormOpen, setIsDomainMappingFormOpen] = useState(false);
  const [editingDomainMapping, setEditingDomainMapping] = useState<DomainMapping | null>(null);
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  
  // No filtering needed since we have a single config
  const filteredBackends = backends;
  const filteredMappings = domainMappings;

  // Add state for backend stats
  const [backendStats, setBackendStats] = useState<any>(null);
  const statsSSERef = useRef<EventSource | null>(null);

  // Load data on component mount
  useEffect(() => {
    fetchInstances();
    fetchBackends();
    fetchDomainMappings();
    fetchTemplates();
  }, []);

  // Initialize SSE connection
  useEffect(() => {
    console.log('HAProxyPage: Initializing SSE connection');
    
    // Handle instance status updates
    const handleInstanceStatus = (statusData: { 
      instanceId: string, 
      status: string, 
      lastChecked: string,
      apiReachable?: boolean,
      serviceStatus?: string
    }) => {
      console.log(`HAProxyPage: Received status update for instance ${statusData.instanceId}`);
      
      setInstances(prev => 
        prev.map(instance => {
          if (instance.id === statusData.instanceId) {
            if (statusData.status === 'unauthorized') {
              return {
                ...instance,
                status: 'unauthorized',
                lastChecked: statusData.lastChecked,
                serviceStatus: 'unknown'
              };
            }
            const isRunning = statusData.status === 'online' || statusData.serviceStatus === 'started';
            return {
              ...instance,
              status: isRunning ? 'online' : 'offline',
              lastChecked: statusData.lastChecked,
              serviceStatus: isRunning ? 'started' : 'stopped'
            };
          }
          return instance;
        })
      );
    };

    // Register event handler
    sseService.on('instanceStatus', handleInstanceStatus);

    // Backend update/delete SSE handlers
    const handleBackendUpdated = (data: { id: string, name: string }) => {
      console.log('SSE: backend_updated received', data);
      fetchDomainMappings();
    };
    const handleBackendDeleted = (data: { id: string }) => {
      console.log('SSE: backend_deleted received', data);
      fetchDomainMappings();
    };
    sseService.on('backend_updated', handleBackendUpdated);
    sseService.on('backend_deleted', handleBackendDeleted);

    // Cleanup function - only remove the event handler, don't close the connection
    return () => {
      console.log('HAProxyPage: Cleaning up SSE event handlers');
      sseService.off('instanceStatus', handleInstanceStatus);
      sseService.off('backend_updated', handleBackendUpdated);
      sseService.off('backend_deleted', handleBackendDeleted);
      // Don't close the connection here as it might be used by other components
      // sseService.close();
    };
  }, []); // Empty dependency array means this runs once on mount

  useEffect(() => {
    function openStatsSSE() {
      if (statsSSERef.current) return;
      statsSSERef.current = new EventSource(`${API_BASE_URL}/stats-sse`);
      statsSSERef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setBackendStats(data);
          // For now, just log
          // console.log('Received backend stats:', data);
        } catch (e) {
          // Ignore
        }
      };
    }
    function closeStatsSSE() {
      if (statsSSERef.current) {
        statsSSERef.current.close();
        statsSSERef.current = null;
      }
    }
    function handleVisibilityOrTab() {
      if (activeTab === 1 && document.visibilityState === 'visible') {
        openStatsSSE();
      } else {
        closeStatsSSE();
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityOrTab);
    handleVisibilityOrTab(); // Run on mount and whenever activeTab changes
    return () => {
      closeStatsSSE();
      document.removeEventListener('visibilitychange', handleVisibilityOrTab);
    };
  }, [activeTab]);

  // Fetch data functions
  const fetchInstances = async () => {
    try {
      setLoading(prev => ({ ...prev, instances: true }));
      const data = await api.instances.getAll();
      setInstances(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch instances:', err);
      setError('Failed to load instances. Please try again later.');
    } finally {
      setLoading(prev => ({ ...prev, instances: false }));
    }
  };

  const fetchBackends = async () => {
    try {
      setLoading(prev => ({ ...prev, backends: true }));
      const data = await api.backends.getAll();
      setBackends(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch backends:', err);
      setError('Failed to load backends. Please try again later.');
    } finally {
      setLoading(prev => ({ ...prev, backends: false }));
    }
  };

  const fetchDomainMappings = async () => {
    try {
      setLoading(prev => ({ ...prev, domainMappings: true }));
      const data = await api.domainMappings.getAll();
      setDomainMappings(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch domain mappings:', err);
      setError('Failed to load domain mappings. Please try again later.');
    } finally {
      setLoading(prev => ({ ...prev, domainMappings: false }));
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(prev => ({ ...prev, templates: true }));
      const data = await api.templates.getAll();
      setTemplates(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      setError('Failed to load templates. Please try again later.');
    } finally {
      setLoading(prev => ({ ...prev, templates: false }));
    }
  };

  // Instance CRUD Actions
  const handleAddInstance = () => {
    setEditingInstance(null);
    setIsFormOpen(true);
  };

  const handleEditInstance = (id: string) => {
    const instance = instances.find(instance => instance.id === id);
    if (instance) {
      setEditingInstance(instance);
      setIsFormOpen(true);
    }
  };

  const handleDeleteInstance = async (id: string) => {
    try {
      await api.instances.delete(id);
      setInstances(prevInstances => prevInstances.filter(instance => instance.id !== id));
      setError(null);
    } catch (err) {
      console.error('Failed to delete instance:', err);
      setError('Failed to delete instance. Please try again later.');
    }
  };

  const handleRefreshStatus = async (id: string) => {
    try {
      // In a real implementation, you might have an API endpoint to check status
      // For now, we'll simulate it with random values
      const currentDateTime = new Date().toLocaleString();
      
      setInstances(prevInstances => 
        prevInstances.map(instance => {
          if (instance.id === id) {
            // Randomly choose a status for demo purposes
            const randomValue = Math.random();
            let newStatus: 'online' | 'offline' | 'degraded' = 'online';
            
            if (randomValue > 0.7) {
              newStatus = randomValue > 0.5 ? 'degraded' : 'offline';
            }
            
            return { 
              ...instance,
              lastChecked: currentDateTime,
              status: newStatus
            };
          }
          return instance;
        })
      );
    } catch (err) {
      console.error('Failed to refresh instance status:', err);
      setError('Failed to refresh instance status. Please try again later.');
    }
  };

  const handleSaveInstance = async (instance: { id?: string; name: string; host: string; port: number; apiKey?: string }) => {
    try {
      if (editingInstance) {
        // Update existing instance
        const updatedInstance = await api.instances.update(instance.id!, {
          name: instance.name,
          host: instance.host,
          port: instance.port,
          apiKey: instance.apiKey || undefined
        });
        setInstances(prevInstances => 
          prevInstances.map(item => 
            item.id === updatedInstance.id ? updatedInstance : item
          )
        );
      } else {
        // Add new instance
        const newInstance = await api.instances.create({
          name: instance.name,
          host: instance.host,
          port: instance.port,
          apiKey: instance.apiKey || undefined
        });
        setInstances(prevInstances => [...prevInstances, newInstance]);
      }
      setIsFormOpen(false);
      setEditingInstance(null);
      setError(null);
    } catch (err) {
      console.error('Failed to save instance:', err);
      setError('Failed to save instance. Please try again later.');
    }
  };

  const handleCancelForm = () => {
    setIsFormOpen(false);
    setEditingInstance(null);
  };

  // Backend CRUD Actions
  const handleAddBackend = async (backend: Omit<HAProxyBackend, 'id'> & { servers: HAProxyServer[] }) => {
    try {
      // Create new backend
      const newBackend = await api.backends.create({
        name: backend.name,
        balance: backend.balance,
        servers: backend.servers
      });
      setBackends(prevBackends => [...prevBackends, newBackend]);
      setError(null);
    } catch (err) {
      console.error('Failed to add backend:', err);
      setError('Failed to add backend. Please try again later.');
    }
  };

  const handleEditBackend = async (updatedBackend: HAProxyBackend) => {
    try {
      const savedBackend = await api.backends.update(updatedBackend.id, {
        name: updatedBackend.name,
        balance: updatedBackend.balance,
        servers: updatedBackend.servers || []
      });
      setBackends(prevBackends => 
        prevBackends.map(backend => 
          backend.id === savedBackend.id ? savedBackend : backend
        )
      );
      setError(null);
    } catch (err) {
      console.error('Failed to update backend:', err);
      setError('Failed to update backend. Please try again later.');
    }
  };

  const handleDeleteBackend = async (id: string) => {
    try {
      await api.backends.delete(id);
      setBackends(prevBackends => prevBackends.filter(backend => backend.id !== id));
      setError(null);
    } catch (err) {
      console.error('Failed to delete backend:', err);
      setError('Failed to delete backend. Please try again later.');
    }
  };

  // Domain Mapping Actions
  const handleAddMapping = () => {
    setEditingDomainMapping(null);
    setIsDomainMappingFormOpen(true);
  };

  const handleEditMapping = (id: string) => {
    const mapping = domainMappings.find(mapping => mapping.id === id);
    if (mapping) {
      setEditingDomainMapping(mapping);
      setIsDomainMappingFormOpen(true);
    }
  };

  const handleSaveDomainMapping = async (
    mappingData: Omit<DomainMapping, 'id' | 'backendName' | 'active' | 'sslEnabled'>, 
    backendName: string
  ) => {
    try {
      if (editingDomainMapping) {
        // Update existing mapping
        const updatedMapping = await api.domainMappings.update(editingDomainMapping.id, {
          domain: mappingData.domain,
          pattern: mappingData.pattern || '',
          backendId: mappingData.backendId,
          backendName: backendName,
          priority: mappingData.priority || 10,
          active: editingDomainMapping.active,
          sslEnabled: editingDomainMapping.sslEnabled
        });
        
        setDomainMappings(prevMappings => 
          prevMappings.map(item => 
            item.id === updatedMapping.id ? updatedMapping : item
          )
        );
      } else {
        // Add new mapping
        const nextPriority = (domainMappings.length > 0 ? Math.max(...domainMappings.map(m => m.priority)) + 1 : 1);
        const newMapping = await api.domainMappings.create({
          domain: mappingData.domain,
          pattern: mappingData.pattern || '',
          backendId: mappingData.backendId,
          backendName: backendName,
          priority: nextPriority,
          active: true,
          sslEnabled: false
        });
        setDomainMappings(prevMappings => [...prevMappings, newMapping]);
      }
      
      setIsDomainMappingFormOpen(false);
      setEditingDomainMapping(null);
      setError(null);
    } catch (err) {
      console.error('Failed to save domain mapping:', err);
      setError('Failed to save domain mapping. Please try again later.');
    }
  };

  const handleCancelDomainMappingForm = () => {
    setIsDomainMappingFormOpen(false);
    setEditingDomainMapping(null);
  };

  const handleDeleteMapping = async (id: string) => {
    try {
      await api.domainMappings.delete(id);
      setDomainMappings(prevMappings => prevMappings.filter(mapping => mapping.id !== id));
      setError(null);
    } catch (err) {
      console.error('Failed to delete domain mapping:', err);
      setError('Failed to delete domain mapping. Please try again later.');
    }
  };

  const handleToggleMappingStatus = async (id: string, active: boolean) => {
    // Optimistically update the UI
    setDomainMappings(prevMappings =>
      prevMappings.map(mapping =>
        mapping.id === id ? { ...mapping, active } : mapping
      )
    );
    setError(null);

    try {
      await api.domainMappings.toggleStatus(id, active);
      // No re-fetch or loading spinner
    } catch (err) {
      // Revert the change if the API call fails
      setDomainMappings(prevMappings =>
        prevMappings.map(mapping =>
          mapping.id === id ? { ...mapping, active: !active } : mapping
        )
      );
      setError('Failed to toggle domain mapping status. Please try again later.');
    }
  };
  
  // Reorder domain mappings via drag and drop
  const handleReorderMappings = async (sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;
    setDomainMappings(prevMappings => {
      const mappingsCopy = [...prevMappings];
      const sourceIndex = mappingsCopy.findIndex(mapping => mapping.id === sourceId);
      const targetIndex = mappingsCopy.findIndex(mapping => mapping.id === targetId);
      if (sourceIndex === -1 || targetIndex === -1) return prevMappings;
      // Remove the source mapping
      const [removed] = mappingsCopy.splice(sourceIndex, 1);
      // Insert it at the target position
      mappingsCopy.splice(targetIndex, 0, removed);
      // Recalculate priorities (1-based)
      const priorities = mappingsCopy.map((m, idx) => ({ id: m.id, priority: idx + 1 }));
      // Update local state
      const updated = mappingsCopy.map((m, idx) => ({ ...m, priority: idx + 1 }));
      // Send to backend
      api.domainMappings.reorder(priorities).catch(() => {
        setError('Failed to update domain mapping order. Please try again later.');
      });
      return updated;
    });
  };

  // Template CRUD Actions
  const handleAddTemplate = async (template: Omit<HAProxyTemplate, 'id' | 'createdAt' | 'updatedAt' | 'lastUsedAt'>) => {
    try {
      const newTemplate = await api.templates.create(template);
      setTemplates(prevTemplates => [...prevTemplates, newTemplate]);
      setError(null);
    } catch (err) {
      console.error('Failed to add template:', err);
      setError('Failed to add template. Please try again later.');
    }
  };

  const handleUpdateTemplate = async (id: string, template: Omit<HAProxyTemplate, 'id' | 'createdAt' | 'updatedAt' | 'lastUsedAt'>) => {
    try {
      const updatedTemplate = await api.templates.update(id, template);
      setTemplates(prevTemplates =>
        prevTemplates.map(t => t.id === id ? updatedTemplate : t)
      );
      setError(null);
    } catch (err) {
      console.error('Failed to update template:', err);
      setError('Failed to update template. Please try again later.');
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    try {
      await api.templates.delete(id);
      setTemplates(prevTemplates => prevTemplates.filter(t => t.id !== id));
      setError(null);
    } catch (err) {
      console.error('Failed to delete template:', err);
      setError('Failed to delete template. Please try again later.');
    }
  };

  const handleSetDefaultTemplate = async (id: string) => {
    try {
      await api.templates.setDefault(id);
      setError(null);
      // Refresh templates to reflect new default
      await fetchTemplates();
    } catch (err) {
      console.error('Failed to set default template:', err);
      setError('Failed to set default template. Please try again later.');
    }
  };

  // Find the default template
  const defaultTemplate = templates.find(t => t.isDefault) || templates[0] || null;

  // Update deploy button to select default template
  const handleOpenDeployModal = () => {
    setSelectedTemplateId(defaultTemplate ? defaultTemplate.id : null);
    setIsDeployModalOpen(true);
  };

  // Add this function to handle deploy from TemplatesList
  const handleDeployTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setIsDeployModalOpen(true);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      {error && (
        <div className="bg-red-500 text-white p-4 rounded-md mb-6">
          {error}
        </div>
      )}
      
      {/* Instance form modal */}
      <InstanceForm 
        instance={editingInstance || undefined} 
        onSave={handleSaveInstance}
        onCancel={handleCancelForm}
        isOpen={isFormOpen}
      />

      {/* Domain mapping form modal */}
      <DomainMappingForm
        mapping={editingDomainMapping || undefined}
        backends={backends}
        onSave={handleSaveDomainMapping}
        onCancel={handleCancelDomainMappingForm}
        isOpen={isDomainMappingFormOpen}
      />

      {/* Enterprise-grade header and navigation */}
      <div className="mb-8">
        {/* Primary header */}
        <div className="relative shadow-sm">
          <div className="mx-auto px-4 sm:px-6 lg:px-0">
            <div className="flex h-16 items-center justify-between">
              {/* Left: Logo and title */}
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded bg-indigo-500 flex items-center justify-center">
                    <ServerIcon className="h-5 w-5 text-white" aria-hidden="true" />
                  </div>
                </div>
                <div>
                  <h1 className="text-xl font-semibold tracking-tight text-zinc-100">HAProxy Management</h1>
                  <p className="text-xs text-zinc-400">Enterprise Configuration</p>
                </div>
              </div>

              {/* Right: Status and actions */}
              <div className="flex items-center space-x-4">
                {/* Status indicators */}
                <div className="hidden md:flex items-center space-x-4 border-r border-zinc-700 pr-4">
                  <div className="flex flex-col justify-center items-center px-3 py-1">
                    <div className="text-lg font-semibold text-zinc-100">{instances.length}</div>
                    <div className="text-xs text-zinc-400">Instances</div>
                  </div>
                  
                  <div className="flex flex-col justify-center items-center px-3 py-1 border-l border-zinc-700 pl-4">
                    <div className="flex items-center">
                      {instances.some(i => i.status === 'online') ? (
                        <div className="h-2 w-2 rounded-full bg-green-500 mr-2"></div>
                      ) : (
                        <div className="h-2 w-2 rounded-full bg-red-500 mr-2"></div>
                      )}
                      <span className="text-zinc-100 text-xs font-medium">
                        {instances.some(i => i.status === 'online') ? 'ONLINE' : 'OFFLINE'}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-400">Service Status</div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setIsHistoryModalOpen(true)}
                    className="inline-flex items-center rounded-md bg-zinc-800 border border-zinc-700 px-3 py-1.5 text-sm font-medium text-zinc-200 shadow-sm hover:bg-zinc-700 transition-colors duration-150 ease-in-out focus:outline-none"
                  >
                    <ClockIcon className="h-4 w-4 mr-1.5" aria-hidden="true" />
                    History
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenDeployModal}
                    className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 transition-colors duration-150 ease-in-out focus:outline-none"
                  >
                    <CloudArrowUpIcon className="h-4 w-4 mr-1.5" aria-hidden="true" />
                    Deploy
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab navigation with polish */}
        <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
          <div className="border-b border-zinc-800/80">
            <div className="mx-auto px-4 sm:px-6 lg:px-0">
              <Tab.List className="flex -mb-px space-x-8">
                {tabLabels.map((tab) => (
                <Tab
                    key={tab.label}
                  className={({ selected }) =>
                      `group relative py-4 px-1 text-sm font-medium outline-none transition-colors duration-300 \
                      ${selected ? 'text-indigo-400' : 'text-zinc-400 hover:text-zinc-200'}`
                  }
                >
                  {({ selected }) => (
                    <>
                      <div className="flex items-center">
                          <tab.icon className="h-4 w-4 mr-1.5" aria-hidden="true" />
                          <span className="whitespace-nowrap">{tab.label}</span>
                      </div>
                      <span
                          className={`
                            pointer-events-none absolute left-0 right-0 bottom-0 h-0.5
                            bg-indigo-500 rounded
                            transition-transform duration-300
                            ${selected ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'}
                          `}
                          style={{ transformOrigin: 'center' }}
                      />
                    </>
                  )}
                </Tab>
                ))}
              </Tab.List>
            </div>
          </div>
          {/* Tab panels with consistent padding */}
          <div className="mx-auto px-4 sm:px-6 lg:px-0 pt-6">
            <Tab.Panels>
              <Tab.Panel>
                {loading.instances ? (
                  <div className="flex justify-center items-center py-24 bg-zinc-900/30 rounded-lg border border-zinc-800">
                    <div className="flex flex-col items-center">
                      <div className="spinner mb-4"></div>
                      <p className="text-zinc-400">Loading instances...</p>
                    </div>
                  </div>
                ) : (
                  <InstancesList 
                    instances={instances}
                    onAddInstance={handleAddInstance}
                    onEditInstance={handleEditInstance}
                    onDeleteInstance={handleDeleteInstance}
                    onRefreshStatus={handleRefreshStatus}
                  />
                )}
              </Tab.Panel>
              
              <Tab.Panel>
                {loading.backends ? (
                  <div className="flex justify-center items-center py-24 bg-zinc-900/30 rounded-lg border border-zinc-800">
                    <div className="flex flex-col items-center">
                      <div className="spinner mb-4"></div>
                      <p className="text-zinc-400">Loading backends...</p>
                    </div>
                  </div>
                ) : (
                  <BackendsList 
                    backends={filteredBackends}
                    stats={backendStats?.backends}
                    onAddBackend={handleAddBackend}
                    onEditBackend={handleEditBackend}
                    onDeleteBackend={handleDeleteBackend}
                  />
                )}
              </Tab.Panel>
              
              <Tab.Panel>
                {loading.domainMappings ? (
                  <div className="flex justify-center items-center py-24 bg-zinc-900/30 rounded-lg border border-zinc-800">
                    <div className="flex flex-col items-center">
                      <div className="spinner mb-4"></div>
                      <p className="text-zinc-400">Loading domain mappings...</p>
                    </div>
                  </div>
                ) : (
                  <DomainMappingsList 
                    mappings={filteredMappings}
                    onAddMapping={handleAddMapping}
                    onEditMapping={handleEditMapping}
                    onDeleteMapping={handleDeleteMapping}
                    onToggleStatus={handleToggleMappingStatus}
                    onReorderMappings={handleReorderMappings}
                  />
                )}
              </Tab.Panel>
              
              <Tab.Panel>
                {loading.templates ? (
                  <div className="flex justify-center items-center py-24 bg-zinc-900/30 rounded-lg border border-zinc-800">
                    <div className="flex flex-col items-center">
                      <div className="spinner mb-4"></div>
                      <p className="text-zinc-400">Loading templates...</p>
                    </div>
                  </div>
                ) : (
                  <TemplatesList
                    templates={templates}
                    instances={instances}
                    onAddTemplate={handleAddTemplate}
                    onUpdateTemplate={handleUpdateTemplate}
                    onDeleteTemplate={handleDeleteTemplate}
                    onSetDefaultTemplate={handleSetDefaultTemplate}
                    onDeployTemplate={handleDeployTemplate}
                  />
                )}
              </Tab.Panel>
            </Tab.Panels>
          </div>
        </Tab.Group>
      </div>
      
      {/* Deployment Modals */}
      {selectedTemplateId && (
        <DeploymentModal
          isOpen={isDeployModalOpen}
          onClose={() => {
            setIsDeployModalOpen(false);
            setSelectedTemplateId(null);
          }}
          instances={instances}
          templateId={selectedTemplateId}
          templates={templates}
          onChangeTemplate={setSelectedTemplateId}
        />
      )}
      
      <DeploymentHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
      />
    </div>
  );
};

export default HAProxyPage; 